import React from "react";
import AnimationRevealPage from "../../../../helpers/AnimationRevealPage.js";
import tw from "twin.macro";
import Header from "../../../../Components/headers/PageHeader";
import MainFeature1 from "../../../../Components/Features/TwoColWithButton.js";
import Features from "../../../../Components/Features/ThreeColumnPages";
import {
  FaVideo,
  FaEye,
  FaRocket,
  FaMobileAlt,
  FaStar,
  FaBriefcase,
} from "react-icons/fa";

// Styled Components
const Subheading = tw.span`uppercase tracking-wider text-sm`;
const Heading = tw.h1`text-2xl md:text-4xl lg:text-5xl font-bold`;
const Description = tw.p`text-base md:text-lg lg:text-xl`;

export const BusinessVideoProductionInAlBarsha = () => {
  return (
    <AnimationRevealPage>
      {/* Header Section */}
      <Header from="Business Video Production in Al Barsha" />

      {/* Hero Section */}
      <MainFeature1
        subheading={<Subheading>Elevate Your Business</Subheading>}
        heading={
          <Heading>
            Professional{" "}
            <span tw="text-primary-500">Business Video Production</span> in Al
            Barsha
          </Heading>
        }
        description={
          <Description>
            At MilaajBrandset, our business video production services in **Al
            Barsha** deliver professional, high-impact videos that showcase your
            brand’s story and drive engagement. From corporate promos to product
            showcases, we create content that fuels growth.
          </Description>
        }
        imageSrcs={[
          "https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?w=800&auto=format&fit=crop&q=60", // Team meeting
          "https://images.pexels.com/photos/3182746/pexels-photo-3182746.jpeg?w=800&auto=format&fit=crop&q=60", // Video production
          "https://images.pexels.com/photos/3184297/pexels-photo-3184297.jpeg?w=800&auto=format&fit=crop&q=60", // Professional visuals
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Start Your Project"
        buttonRounded={true}
        textOnLeft={false}
      />

      {/* Key Benefits Section */}
      <Features
        subheading={<Subheading>Why Choose Us?</Subheading>}
        heading="Expert Business Video Production in Al Barsha"
        description="We craft business videos that blend professionalism with strategic impact."
        cards={[
          {
            icon: <FaBriefcase color="#ff5464" size={40} />,
            title: "Brand-Aligned",
            description:
              "Videos that reflect your business identity and goals.",
          },
          {
            icon: <FaEye color="#ff5464" size={40} />,
            title: "Engaging Content",
            description: "Compelling visuals that captivate your audience.",
          },
          {
            icon: <FaRocket color="#ff5464" size={40} />,
            title: "Efficient Delivery",
            description: "Timely production to meet your business needs.",
          },
          {
            icon: <FaMobileAlt color="#ff5464" size={40} />,
            title: "Platform-Optimized",
            description:
              "Content tailored for websites, social media, and pitches.",
          },
          {
            icon: <FaStar color="#ff5464" size={40} />,
            title: "Trusted Partner",
            description: "Al Barsha’s choice for business videography.",
          },
          {
            icon: <FaVideo color="#ff5464" size={40} />,
            title: "End-to-End Service",
            description: "From concept to final edit, we’ve got you covered.",
          },
        ]}
      />

      {/* Our Process Section */}
      <MainFeature1
        subheading={<Subheading>How We Work</Subheading>}
        heading="Our Business Video Production Process"
        description={
          <Description>
            We deliver professional business videos through a refined process:
            <ul tw="mt-4 list-disc pl-6">
              <li>
                Consultation: Align with your business objectives and vision.
              </li>
              <li>
                Storyboarding: Craft a narrative that highlights your brand.
              </li>
              <li>
                Filming: Capture high-quality footage with advanced equipment.
              </li>
              <li>
                Editing: Enhance with effects, sound, and branding elements.
              </li>
              <li>Review: Ensure the video meets your expectations.</li>
              <li>
                Delivery: Provide polished content ready for your platforms.
              </li>
            </ul>
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1504465039710-0f49c0a47eb7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80", // Video editing workspace
          "https://images.unsplash.com/photo-1522152716425-ec958d91dbf2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80", // Professional camera equipment
          "https://images.unsplash.com/photo-1581591524425-c7e0978865fc?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80", // Video production planning meeting
        ]}
        widths={["60%", "55%", "70%"]}
        heights={["60%", "45%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "right: 30%; top: 10%;",
          "left: 35%; bottom: 20%;",
          "right: 32%; bottom: 10%;",
        ]}
        primaryButtonText="Learn More"
        buttonRounded={true}
        textOnLeft={true}
      />

      {/* Success Stories Section */}
      <Features
        subheading={<Subheading>Success Stories</Subheading>}
        heading="Business Video Wins in Al Barsha"
        description="Our business videos have driven growth for brands in Al Barsha."
        cards={[
          {
            icon: <FaVideo color="#ff5464" size={40} />,
            title: "Increased Engagement",
            description: "Boosted client interaction with a brand video.",
          },
          {
            icon: <FaEye color="#ff5464" size={40} />,
            title: "Enhanced Visibility",
            description: "Elevated a local business’s online presence.",
          },
          {
            icon: <FaStar color="#ff5464" size={40} />,
            title: "Client Satisfaction",
            description: "97% approval from business clients.",
          },
        ]}
      />

      {/* Call-to-Action Section */}
      <MainFeature1
        subheading={<Subheading>Ready for Professional Videos?</Subheading>}
        heading="Business Video Production in Al Barsha"
        description={
          <Description>
            Partner with MilaajBrandset for professional business video
            production in Al Barsha. Create high-impact videos that showcase
            your brand and drive growth—contact us today!
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1513759565286-20e9c5fad06b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80", // Dubai business district skyline
          "https://images.unsplash.com/photo-1528109966604-5a6a3a443625?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80", // Professional videographer with equipment
          "https://images.unsplash.com/photo-1569159316193-42ecbf194bad?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80", // Corporate video interview setup
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Contact Us Now"
        buttonRounded={true}
        textOnLeft={false}
      />
    </AnimationRevealPage>
  );
};
